import CheckCircle2 from 'lucide-react/dist/esm/icons/check-circle-2'
import FileText from 'lucide-react/dist/esm/icons/file-text'
import XCircle from 'lucide-react/dist/esm/icons/x-circle'

import { DeleteDocumentButton } from '@/components/documents/DeleteDocumentButton'
import { formatFileSize } from '@/lib/utils'
import { isRejectedDocument, type RiskPortalExternalDocument } from '@/types/riskPortal.types'

interface UploadedFileRowProps {
  document: RiskPortalExternalDocument
  onRemove: () => void
  isRemoving: boolean
}

/**
 * A file the client has already uploaded. Shows the green "Received" state, or — when a reviewer rejected
 * the file — a red "please re-upload" state with the reviewer's reason. Internal workflow states
 * (Moved/Accepted) still render as "Received"; clients only need to know their file arrived.
 */
export function UploadedFileRow({ document, onRemove, isRemoving }: UploadedFileRowProps) {
  if (isRejectedDocument(document)) {
    return (
      <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-3">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/70"
            aria-hidden="true"
          >
            <FileText className="h-5 w-5 text-destructive" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{document.fileName}</p>
            <p className="flex items-center gap-1 text-xs text-destructive">
              <XCircle className="h-3.5 w-3.5" />
              Rejected — please re-upload · {formatFileSize(document.fileSizeBytes)}
            </p>
          </div>
          <DeleteDocumentButton
            fileName={document.fileName}
            onConfirmDelete={onRemove}
            isDeleting={isRemoving}
          />
        </div>
        {document.rejectionReason ? (
          <p className="mt-2 rounded-lg bg-white/60 px-3 py-2 text-xs leading-relaxed text-foreground/80">
            {document.rejectionReason}
          </p>
        ) : null}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-3 rounded-xl border border-success-border/30 bg-success-bg/40 p-3">
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/70"
        aria-hidden="true"
      >
        <FileText className="h-5 w-5 text-success-border" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{document.fileName}</p>
        <p className="flex items-center gap-1 text-xs text-success-border">
          <CheckCircle2 className="h-3.5 w-3.5" />
          Received · {formatFileSize(document.fileSizeBytes)}
        </p>
      </div>
      <DeleteDocumentButton
        fileName={document.fileName}
        onConfirmDelete={onRemove}
        isDeleting={isRemoving}
      />
    </div>
  )
}
